import { format } from 'date-fns';

/**
 * Has the review decision on a plan or signage package been sent to the tenant?
 *
 * The tenant cannot sign in. The decision is emailed to the Property Manager
 * and APM to forward, and forwarding it happens in Outlook, where the Hub
 * cannot see it - so the Property Manager records it here, by hand.
 *
 * The record is an audit trail entry, not a column: the audit trail is already
 * the history of a submission, the send is one more event in it, and keeping
 * it there needs no migration. Each decision round is its own question. A
 * "Revise & Resubmit" sent to the tenant does not cover the approval that
 * follows the resubmission, so a send counts only if it comes after the latest
 * decision.
 */

export const SENT_TO_TENANT = 'sent_to_tenant';

/** The statuses a decision leaves a submission in - the ones with something to send. */
const DECIDED = new Set(['approved', 'revisions_required', 'rejected']);

function lastIndexWhere(list, test) {
  for (let i = list.length - 1; i >= 0; i -= 1) {
    if (test(list[i])) return i;
  }
  return -1;
}

/**
 * Where a submission stands with the tenant.
 *
 * `due` is false while there is no decision to send - a draft, or a package
 * in review. Otherwise `sent` is the send recorded for the latest decision,
 * or null when it has not been recorded yet.
 */
export function tenantSendStatus(plan) {
  if (!plan || !DECIDED.has(plan.status)) return { due: false, sent: null };
  const trail = Array.isArray(plan.audit_trail) ? plan.audit_trail : [];
  // The CM's decision is the entry carrying approval_status; see PlanReview.
  const decidedAt = lastIndexWhere(trail, e => e && e.approval_status);
  const sentAt = lastIndexWhere(trail, e => e && e.kind === SENT_TO_TENANT);
  return { due: true, sent: sentAt > decidedAt ? trail[sentAt] : null };
}

/** The audit trail entry that records a send. */
export function sentToTenantEntry({ plan, user, note, now = new Date() }) {
  return {
    action: 'Sent to tenant',
    kind: SENT_TO_TENANT,
    user: user?.email || '',
    user_name: user?.full_name || user?.email || '',
    timestamp: now.toISOString(),
    comment: (note || '').trim(),
    version: plan?.current_revision,
  };
}

/** "Sent to tenant Oct 14, 2026 by Jane Smith" - the date in local time. */
export function sentToTenantLabel(sent) {
  if (!sent) return 'Not yet sent to tenant';
  const who = sent.user_name || sent.user;
  const when = sent.timestamp ? format(new Date(sent.timestamp), 'MMM d, yyyy') : '';
  return ['Sent to tenant', when, who && `by ${who}`].filter(Boolean).join(' ');
}

/** One cell of the report export: blank while nothing has been decided. */
export function tenantSendCell(plan) {
  const status = tenantSendStatus(plan);
  if (!status.due) return '';
  if (!status.sent) return 'Not yet sent';
  return status.sent.timestamp ? format(new Date(status.sent.timestamp), 'MM/dd/yyyy') : 'Sent';
}
